import React from "react";
import { StyledButton, IconWrap, SpinnerWrap } from "./Button.style";

/**
 * Resolve root component: <a> if href, else custom "as", else <button>.
 */
function resolveComponentType(as, href) {
  if (href != null && href !== "") return "a";
  if (as != null) return as;
  return "button";
}

/**
 * Resolve props for the root: type is ignored when href exists; anchor gets href/target/rel.
 */
function resolveRootProps(props) {
  const { type, href, target, rel, as, ...rest } = props;
  const isAnchor = href != null && href !== "";
  const base = { ...rest };
  if (isAnchor) {
    base.href = href;
    if (target != null) base.target = target;
    if (rel != null) base.rel = rel;
  } else if (props.as == null) {
    base.type = type ?? "button";
  }
  return base;
}

/**
 * Render content: leftIcon, loading spinner or children, rightIcon. When loading, children can be hidden or kept.
 * @param {boolean} loadingKeepText - when true and loading, show spinner in front of text (both visible)
 */
function renderContent({ leftIcon, rightIcon, iconOnly, children, loading, loadingKeepText }) {
  const spinner = loading ? (
    <SpinnerWrap aria-hidden>
      <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
        <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" />
      </svg>
    </SpinnerWrap>
  ) : null;

  return (
    <>
      {leftIcon && !loading && <IconWrap>{leftIcon}</IconWrap>}
      {loading && loadingKeepText && <IconWrap>{spinner}</IconWrap>}
      {loading && !loadingKeepText && spinner}
      {loading && !loadingKeepText ? <span style={{ visibility: "hidden", position: "absolute" }}>{children}</span> : null}
      {(!loading || loadingKeepText) && children}
      {rightIcon && !loading && <IconWrap>{rightIcon}</IconWrap>}
    </>
  );
}

/**
 * Button – variant/size/state from Button.variants; behavior rules applied here.
 *
 * Behavior:
 * - loading → disabled automatically
 * - iconOnly → aria-label should be provided (accessibility)
 * - href exists → render as <a>, type ignored
 * - as exists → render as custom element, spread remaining props
 */
export default function Button(props) {
  const {
    children,
    type = "button",
    variant = "primary",
    size = "md",
    disabled = false,
    loading = false,
    loadingKeepText = false,
    fullWidth = false,
    leftIcon,
    rightIcon,
    iconOnly = false,
    onClick,
    as,
    href,
    target,
    rel,
    className,
    rounded = false,
    elevated = false,
    animation = "none",
    "aria-label": ariaLabel,
    ...rest
  } = props;

  const Component = resolveComponentType(as, href);
  const rootProps = resolveRootProps({
    type,
    href,
    target,
    rel,
    as,
    ...rest,
  });

  const isDisabled = disabled || loading;

  if (process.env.NODE_ENV !== "production" && iconOnly && !ariaLabel && !rest["aria-label"]) {
    console.warn(
      "[Button] iconOnly is true but aria-label (or aria-label prop) was not provided. Add aria-label for accessibility."
    );
  }

  const handleClick = (e) => {
    if (Component === "a" && isDisabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <StyledButton
      as={Component}
      $variant={variant}
      $size={size}
      $disabled={isDisabled}
      $loading={loading}
      $fullWidth={fullWidth}
      $rounded={rounded}
      $elevated={elevated}
      $animation={animation}
      className={className}
      disabled={Component === "button" ? isDisabled : undefined}
      aria-disabled={Component === "a" && isDisabled ? true : undefined}
      onClick={handleClick}
      aria-busy={loading}
      aria-label={ariaLabel ?? rest["aria-label"]}
      {...rootProps}
    >
      {renderContent({
        leftIcon,
        rightIcon,
        iconOnly,
        children,
        loading,
        loadingKeepText,
      })}
    </StyledButton>
  );
}
